import { useEffect, useRef, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@app/components/ui/card";
import { Button } from "@app/components/ui/button";
import { Badge } from "@app/components/ui/badge";
import { Input } from "@app/components/ui/input";
import { Label } from "@app/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@app/components/ui/dialog";
import {
  Plus,
  Pencil,
  Trash2,
  ChevronDown,
  ChevronUp,
  BookOpen,
  Layers,
  Loader2,
  Brain,
  Sparkles,
  Upload,
} from "lucide-react";
import { Trans, useLingui } from "@lingui/macro";
import ClinicianFeedbackPanel from "@app/components/ClinicianFeedbackPanel";

// Emoji par groupe d'ontologie (cohérent avec dashboard & patient)
// Fallback when a group has no field carrying a ``group_emoji`` value yet.
const DEFAULT_GROUP_EMOJI = "📁";

// Types pour l'ontologie
export type OntologyOutputConstraints =
  | Record<string, never>
  | { values: string[] }
  | { min?: number; max?: number; decimals?: number; unit?: string }
  | Record<string, unknown>;

export interface OntologyField {
  id: string;
  name: string;
  description: string;
  /**
   * Short, user-facing one-liner shown on the patient page (tooltip "?" and
   * pencil edit modal). The long ``description`` keeps the LLM prompt details
   * and is only displayed inside this OntologyManager.
   */
  summary: string;
  fieldType: "patient" | "content" | "metrics";
  /**
   * Type de sortie attendu pour l'extraction LLM.
   * Source unique de vérité (post-074) — drives both the Pydantic schema
   * generation server-side and the form widget shown to the user.
   */
  evaluatorType: string | null;
  /**
   * Contraintes structurées propres au type :
   *  - enum / array_enum   → { values: string[] }
   *  - number              → { min?, max?, decimals?, unit? }
   *  - boolean / string    → {}
   */
  outputConstraints: OntologyOutputConstraints;
  shots: OntologyShot[];
  fieldKey: string | null;
  groupName: string | null;
  groupEmoji: string | null;
  weight: number;
  /**
   * Combien de propositions d'exemple des médecins attendent sur ce champ.
   *
   * Servie avec la LISTE des champs et non demandée champ par champ : tout l'intérêt du
   * nombre est de dire lequel des cinquante champs les cliniciens attendent, et le demander
   * un par un ferait payer cinquante requêtes pour obtenir cette réponse.
   */
  pendingProposals: number;
}

// 6 user-facing types kept on screen; "json" / "soft_enum" / "score_series"
// remain available via the evaluator field but are advanced-only.
type OutputTypeKey =
  | "string"
  | "boolean"
  | "number"
  | "enum"
  | "array_enum"
  | "array_string";

const OUTPUT_TYPE_OPTIONS: ReadonlyArray<{ key: OutputTypeKey; label: string }> = [
  { key: "string", label: "Texte" },
  { key: "boolean", label: "Booléen" },
  { key: "number", label: "Nombre" },
  { key: "enum", label: "Énumération" },
  { key: "array_enum", label: "Liste d'énumérations" },
  { key: "array_string", label: "Liste de textes" },
];

/**
 * Un champ tel qu'un FORMULAIRE le produit.
 *
 * ``pendingProposals`` en est absent parce qu'il n'est pas une propriété du champ mais une
 * lecture du serveur — combien de médecins attendent sur lui. Le laisser dans le type de
 * création obligerait chaque formulaire à inventer un nombre qu'il ne connaît pas.
 */
export type OntologyFieldDraft = Omit<OntologyField, "id" | "pendingProposals">;

export interface OntologyShot {
  id: string;
  input: string;
  output: string | Record<string, unknown>;
}

interface OntologyManagerProps {
  fields: OntologyField[];
  onAddField: (field: OntologyFieldDraft) => void;
  onUpdateField: (fieldId: string, field: Partial<OntologyField>) => void;
  onDeleteField: (fieldId: string) => void;
  onAddShot: (fieldId: string, shot: Omit<OntologyShot, "id">) => void;
  onUpdateShot: (
    fieldId: string,
    shotId: string,
    shot: Partial<OntologyShot>,
  ) => void;
  onDeleteShot: (fieldId: string, shotId: string) => void;
  onDeployToSpecialite?: () => void;
  deployingToSpecialite?: boolean;
  specialite?: string;
  onEmbedShots?: () => void;
  embeddingShots?: boolean;
  onOptimizeField?: (fieldId: string) => void;
  /**
   * La configuration dont ces champs sont ceux. Nécessaire au panneau des retours
   * cliniciens, qui interroge le serveur par (stack, champ) — un champ n'est pas adressable
   * seul, et c'est ce qui permet au serveur de scoper la lecture à la spécialité du stack.
   */
  stackId: string;
  /** Rechargement de la liste après qu'un retour clinicien est devenu un exemple. */
  onShotsChanged: () => void;
  /**
   * Le champ sur lequel on vient d'atterrir, quand on arrive depuis ailleurs.
   *
   * Sa carte s'ouvre sur les retours cliniciens et défile jusqu'à elle. C'est ce qui rend le
   * lien depuis le classement des corrections utilisable sur une ontologie de cinquante-neuf
   * champs : sans ça, « ouvrir la définition de ce champ » livrerait une liste où il faut
   * retrouver le champ à la main, ce qui est exactement le trajet que le lien supprime.
   */
  focusFieldId?: string | null;
  /** When true, hides all write actions (the demo, a reader visiting another speciality). */
  readOnly?: boolean;
}

// Inline editor for the list of accepted enum values (chips).
function EnumValuesEditor({
  values,
  onChange,
}: {
  values: string[];
  onChange: (next: string[]) => void;
}) {
  const { t } = useLingui();
  const [draft, setDraft] = useState("");

  const addValue = () => {
    const trimmed = draft.trim();
    if (!trimmed || values.includes(trimmed)) {
      setDraft("");
      return;
    }
    onChange([...values, trimmed]);
    setDraft("");
  };

  const removeAt = (idx: number) => {
    onChange(values.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">
        <Trans>Valeurs acceptées</Trans>
      </Label>
      <div className="flex flex-wrap gap-1.5 min-h-[36px] p-2 border border-slate-200 rounded-lg bg-slate-50">
        {values.length === 0 && (
          <span className="text-xs text-slate-400 italic">
            <Trans>Aucune valeur — ajoutez-en ci-dessous</Trans>
          </span>
        )}
        {values.map((v, idx) => (
          <Badge
            key={`${v}-${idx}`}
            variant="default"
            className="cursor-default gap-1 pr-1"
          >
            <span>{v}</span>
            <button
              type="button"
              onClick={() => removeAt(idx)}
              className="ml-1 text-white/80 hover:text-white"
              aria-label={t`Retirer la valeur ${v}`}
            >
              ×
            </button>
          </Badge>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addValue();
            }
          }}
          placeholder={t`Saisir puis Entrée pour ajouter`}
          className="flex-1"
        />
        <Button type="button" size="sm" variant="outline" onClick={addValue}>
          <Trans>Ajouter</Trans>
        </Button>
      </div>
    </div>
  );
}

// Inline editor for numeric constraints (min / max / unit / decimals).
function NumericConstraintsEditor({
  constraints,
  onChange,
}: {
  constraints: { min?: number; max?: number; decimals?: number; unit?: string };
  onChange: (next: { min?: number; max?: number; decimals?: number; unit?: string }) => void;
}) {
  const set = (key: "min" | "max" | "decimals" | "unit", value: string) => {
    const next = { ...constraints };
    if (value === "") {
      delete next[key];
    } else if (key === "unit") {
      next.unit = value;
    } else {
      const parsed = Number(value);
      if (!Number.isNaN(parsed)) {
        next[key] = parsed;
      }
    }
    onChange(next);
  };

  return (
    <div className="grid grid-cols-4 gap-3">
      <div>
        <Label className="text-xs text-slate-600">Min</Label>
        <Input
          type="number"
          value={constraints.min ?? ""}
          onChange={(e) => set("min", e.target.value)}
          className="mt-1"
        />
      </div>
      <div>
        <Label className="text-xs text-slate-600">Max</Label>
        <Input
          type="number"
          value={constraints.max ?? ""}
          onChange={(e) => set("max", e.target.value)}
          className="mt-1"
        />
      </div>
      <div>
        <Label className="text-xs text-slate-600">
          <Trans>Décimales</Trans>
        </Label>
        <Input
          type="number"
          min={0}
          value={constraints.decimals ?? ""}
          onChange={(e) => set("decimals", e.target.value)}
          className="mt-1"
        />
      </div>
      <div>
        <Label className="text-xs text-slate-600">
          <Trans>Unité</Trans>
        </Label>
        <Input
          value={constraints.unit ?? ""}
          onChange={(e) => set("unit", e.target.value)}
          placeholder="kg, cm, ..."
          className="mt-1"
        />
      </div>
    </div>
  );
}

// Dialog pour ajouter/modifier un champ d'ontologie
function FieldEditorDialog({
  open,
  onClose,
  onSave,
  initialData,
  title,
}: {
  open: boolean;
  onClose: () => void;
  onSave: (data: Omit<OntologyFieldDraft, "shots">) => void;
  initialData?: OntologyField;
  title?: string;
}) {
  const { t } = useLingui();
  const [name, setName] = useState(initialData?.name || "");
  const [description, setDescription] = useState(
    initialData?.description || "",
  );
  const [summary, setSummary] = useState(initialData?.summary || "");
  const [fieldType, setFieldType] = useState<OntologyField["fieldType"]>(
    initialData?.fieldType || "content",
  );
  const [outputType, setOutputType] = useState<OutputTypeKey>(
    (initialData?.evaluatorType as OutputTypeKey) || "string",
  );
  const [enumValues, setEnumValues] = useState<string[]>(() => {
    const v = (initialData?.outputConstraints as { values?: string[] })?.values;
    return Array.isArray(v) ? v : [];
  });
  const [numericConstraints, setNumericConstraints] = useState<{
    min?: number;
    max?: number;
    decimals?: number;
    unit?: string;
  }>(() => {
    const c = initialData?.outputConstraints as {
      min?: number;
      max?: number;
      decimals?: number;
      unit?: string;
    } | undefined;
    if (!c) return {};
    return {
      min: c.min,
      max: c.max,
      decimals: c.decimals,
      unit: c.unit,
    };
  });
  const [groupName, setGroupName] = useState(initialData?.groupName || "");
  const [groupEmoji, setGroupEmoji] = useState(initialData?.groupEmoji || "");
  const [fieldKey, setFieldKey] = useState(initialData?.fieldKey || "");
  const [weight, setWeight] = useState(initialData?.weight ?? 1);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = () => {
    setError(null);
    if (!name.trim()) {
      setError(t`Le nom est requis`);
      return;
    }

    let constraints: OntologyOutputConstraints = {};
    if (outputType === "enum" || outputType === "array_enum") {
      if (enumValues.length === 0) {
        setError(t`Ajoutez au moins une valeur acceptée pour ce type.`);
        return;
      }
      constraints = { values: enumValues };
    } else if (outputType === "number") {
      const cleaned: typeof numericConstraints = {};
      if (numericConstraints.min !== undefined) cleaned.min = numericConstraints.min;
      if (numericConstraints.max !== undefined) cleaned.max = numericConstraints.max;
      if (numericConstraints.decimals !== undefined) cleaned.decimals = numericConstraints.decimals;
      if (numericConstraints.unit !== undefined && numericConstraints.unit !== "") {
        cleaned.unit = numericConstraints.unit;
      }
      constraints = cleaned;
    }

    onSave({
      name,
      description,
      summary,
      fieldType,
      evaluatorType: outputType,
      outputConstraints: constraints,
      fieldKey: fieldKey.trim() || null,
      groupName: groupName.trim() || null,
      groupEmoji: groupEmoji.trim() || null,
      weight,
    });
    onClose();
  };

  const showEnumEditor = outputType === "enum" || outputType === "array_enum";
  const showNumericEditor = outputType === "number";

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title ?? t`Ajouter un champ`}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Section 1 — Identité */}
          <section className="space-y-3">
            <div>
              <Label className="text-sm font-medium">
                <Trans>Nom *</Trans>
              </Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex. : Diagnostic principal"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">
                <Trans>Résumé</Trans>
              </Label>
              <Input
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                placeholder={t`Phrase courte affichée sur la page patient (info-bulle « ? » et modal d'édition).`}
                className="mt-1"
              />
              <p className="mt-1 text-xs text-slate-500">
                <Trans>
                  Une seule phrase, lisible par l'utilisateur final. Si vide,
                  la description longue sera utilisée à la place.
                </Trans>
              </p>
            </div>
            <div>
              <Label className="text-sm font-medium">
                <Trans>Description *</Trans>
              </Label>
              <textarea
                className="mt-1 w-full min-h-[88px] text-sm border border-slate-200 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-primary/50"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t`Décrire précisément ce que le LLM doit extraire pour ce champ. C'est le levier principal de qualité d'extraction.`}
              />
              <p className="mt-1 text-xs text-slate-500">
                <Trans>
                  Texte envoyé au prompt LLM. Visible uniquement ici, pas sur
                  la page patient.
                </Trans>
              </p>
            </div>
          </section>

          {/* Section 2 — Type de sortie */}
          <section className="space-y-3 border-t border-slate-100 pt-4">
            <Label className="text-sm font-medium">
              <Trans>Type de sortie</Trans>
            </Label>
            <div className="flex flex-wrap gap-1.5">
              {OUTPUT_TYPE_OPTIONS.map((opt) => (
                <Badge
                  key={opt.key}
                  variant={outputType === opt.key ? "default" : "outline"}
                  className="cursor-pointer text-xs"
                  onClick={() => setOutputType(opt.key)}
                >
                  {opt.label}
                </Badge>
              ))}
            </div>

            {showEnumEditor && (
              <EnumValuesEditor values={enumValues} onChange={setEnumValues} />
            )}
            {showNumericEditor && (
              <NumericConstraintsEditor
                constraints={numericConstraints}
                onChange={setNumericConstraints}
              />
            )}
          </section>

          {/* Section 3 — Avancé */}
          <section className="border-t border-slate-100 pt-4">
            <button
              type="button"
              onClick={() => setShowAdvanced((s) => !s)}
              className="flex items-center gap-1 text-sm font-medium text-slate-600 hover:text-slate-900"
            >
              {showAdvanced ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
              <Trans>Avancé</Trans>
            </button>

            {showAdvanced && (
              <div className="mt-3 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm font-medium">
                      <Trans>Sous-ensemble (groupe)</Trans>
                    </Label>
                    <div className="mt-1 flex gap-2">
                      <Input
                        value={groupEmoji}
                        onChange={(e) => setGroupEmoji(e.target.value)}
                        placeholder="📁"
                        aria-label={t`Émoji du groupe`}
                        maxLength={4}
                        className="w-14 text-center text-lg"
                      />
                      <Input
                        value={groupName}
                        onChange={(e) => setGroupName(e.target.value)}
                        placeholder={t`Ex. : Poids et taille, Scores...`}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">
                      <Trans>Clé patient (field_key)</Trans>
                    </Label>
                    <Input
                      value={fieldKey}
                      onChange={(e) => setFieldKey(e.target.value)}
                      placeholder="poids, sexe, ..."
                      className="mt-1 font-mono text-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm font-medium">
                      <Trans>Catégorie</Trans>
                    </Label>
                    <div className="flex gap-2 mt-2">
                      {(["patient", "content", "metrics"] as const).map((type) => (
                        <Badge
                          key={type}
                          variant={fieldType === type ? "default" : "outline"}
                          className="cursor-pointer text-xs capitalize"
                          onClick={() => setFieldType(type)}
                        >
                          {type === "patient"
                            ? t`Patient`
                            : type === "content"
                              ? t`Contenu`
                              : t`Métriques`}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">
                      <Trans>Poids (précision)</Trans>
                    </Label>
                    <Input
                      type="number"
                      min={1}
                      value={weight}
                      onChange={(e) => setWeight(Number(e.target.value))}
                      className="mt-1 w-20"
                    />
                  </div>
                </div>
              </div>
            )}
          </section>

          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded text-sm">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            <Trans>Annuler</Trans>
          </Button>
          <Button onClick={handleSave}>
            <Trans>Enregistrer</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Dialog pour ajouter/modifier un shot (exemple)
function ShotEditorDialog({
  open,
  onClose,
  onSave,
  initialData,
  title,
}: {
  open: boolean;
  onClose: () => void;
  onSave: (data: Omit<OntologyShot, "id">) => void;
  initialData?: OntologyShot;
  title?: string;
}) {
  const { t } = useLingui();
  const [input, setInput] = useState(initialData?.input || "");
  const [outputText, setOutputText] = useState(
    initialData?.output
      ? typeof initialData.output === "string"
        ? initialData.output
        : JSON.stringify(initialData.output, null, 2)
      : "",
  );
  const [isJsonOutput, setIsJsonOutput] = useState(
    initialData?.output ? typeof initialData.output !== "string" : false,
  );
  const [error, setError] = useState<string | null>(null);

  const handleSave = () => {
    setError(null);
    if (!input.trim()) {
      setError(t`L'entrée est requise`);
      return;
    }
    if (!outputText.trim()) {
      setError(t`La sortie est requise`);
      return;
    }

    let output: string | Record<string, unknown> = outputText;
    if (isJsonOutput) {
      try {
        output = JSON.parse(outputText);
      } catch {
        setError(t`JSON de sortie invalide`);
        return;
      }
    }

    onSave({ input, output });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle>{title ?? t`Ajouter un exemple`}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-sm font-medium"><Trans>Entrée (Input) *</Trans></Label>
            <textarea
              className="mt-1 w-full h-32 font-mono text-xs border border-slate-200 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-primary/50"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Texte d'entrée..."
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-sm font-medium"><Trans>Sortie (Output) *</Trans></Label>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500"><Trans>Format :</Trans></span>
                <Badge
                  variant={!isJsonOutput ? "default" : "outline"}
                  className="cursor-pointer text-xs"
                  onClick={() => setIsJsonOutput(false)}
                >
                  <Trans>Texte</Trans>
                </Badge>
                <Badge
                  variant={isJsonOutput ? "default" : "outline"}
                  className="cursor-pointer text-xs"
                  onClick={() => setIsJsonOutput(true)}
                >
                  JSON
                </Badge>
              </div>
            </div>
            <textarea
              className="mt-1 w-full h-32 font-mono text-xs border border-slate-200 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-primary/50"
              value={outputText}
              onChange={(e) => setOutputText(e.target.value)}
              placeholder={
                isJsonOutput ? '{"key": "value"}' : "Texte de sortie..."
              }
            />
          </div>

          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded text-sm">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            <Trans>Annuler</Trans>
          </Button>
          <Button onClick={handleSave}><Trans>Enregistrer</Trans></Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Composant pour afficher un champ d'ontologie avec ses shots
function OntologyFieldCard({
  field,
  stackId,
  focused = false,
  onEdit,
  onDelete,
  onAddShot,
  onEditShot,
  onDeleteShot,
  onOptimize,
  onShotsChanged,
  readOnly,
}: {
  field: OntologyField;
  stackId: string;
  onShotsChanged: () => void;
  focused?: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onAddShot: () => void;
  onEditShot: (shot: OntologyShot) => void;
  onDeleteShot: (shotId: string) => void;
  onOptimize?: () => void;
  readOnly?: boolean;
}) {
  const { t } = useLingui();
  const [expanded, setExpanded] = useState(false);
  // Déplié d'emblée quand on atterrit sur ce champ : c'est ce qu'on est venu voir.
  const [feedbackOpen, setFeedbackOpen] = useState(focused);
  const cardRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!focused) return;
    // `block: "start"` et pas `"center"` : la carte vit dans une modale défilante, et centrer
    // une carte haute laisse son titre au-dessus du bord visible.
    cardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [focused]);

  const typeColor = {
    patient: "bg-blue-100 text-blue-700",
    content: "bg-green-100 text-green-700",
    metrics: "bg-purple-100 text-purple-700",
  };

  return (
    <Card
      ref={cardRef}
      className={
        focused
          ? "overflow-hidden border-l-4 border-l-primary shadow-sm bg-white ring-2 ring-primary/40"
          : "overflow-hidden border-l-4 border-l-primary shadow-sm bg-white"
      }
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle className="text-base">{field.name}</CardTitle>
            <Badge className={`text-xs ${typeColor[field.fieldType]}`}>
              {field.fieldType === "patient"
                ? t`Patient`
                : field.fieldType === "content"
                  ? t`Contenu`
                  : t`Métriques`}
            </Badge>
          </div>
          {!readOnly && (
            <div className="flex gap-1">
              {onOptimize && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onOptimize}
                  title={t`Optimiser avec le LLM`}
                >
                  <Sparkles className="h-4 w-4 text-primary" />
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={onEdit}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onDelete}
                className="text-red-500 hover:text-red-700"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
        {field.description && (
          <p className="text-sm text-slate-500 mt-1">{field.description}</p>
        )}
        <div className="flex items-center gap-4 mt-2 text-xs text-slate-500 flex-wrap">
          <span><Trans>{field.shots.length} exemples</Trans></span>
          {field.groupName && <span><Trans>Groupe : {field.groupName}</Trans></span>}
          {field.evaluatorType && (
            <span><Trans>Type : {field.evaluatorType}</Trans></span>
          )}
          {field.weight > 1 && <span><Trans>Poids : {field.weight}</Trans></span>}
        </div>
      </CardHeader>

      <CardContent className="pt-2">
        {/* Output constraints — render based on evaluator type */}
        {(() => {
          const c = field.outputConstraints as {
            values?: string[];
            min?: number;
            max?: number;
            decimals?: number;
            unit?: string;
          } | null;
          if (!c) return null;

          if (Array.isArray(c.values) && c.values.length > 0) {
            const preview = c.values.slice(0, 6);
            const rest = c.values.length - preview.length;
            return (
              <div className="mb-4">
                <div className="text-xs font-medium text-slate-600 mb-2">
                  <Trans>Valeurs acceptées ({c.values.length})</Trans>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {preview.map((v) => (
                    <Badge key={v} variant="outline" className="text-xs">
                      {v}
                    </Badge>
                  ))}
                  {rest > 0 && (
                    <Badge variant="outline" className="text-xs">
                      <Trans>+{rest} autres</Trans>
                    </Badge>
                  )}
                </div>
              </div>
            );
          }

          const numericBits: string[] = [];
          if (c.min !== undefined) numericBits.push(`min ${c.min}`);
          if (c.max !== undefined) numericBits.push(`max ${c.max}`);
          if (c.decimals !== undefined) numericBits.push(`${c.decimals} décimales`);
          if (c.unit) numericBits.push(c.unit);
          if (numericBits.length > 0) {
            return (
              <div className="mb-4 text-xs text-slate-600">
                <span className="font-medium">
                  <Trans>Contraintes :</Trans>
                </span>{" "}
                {numericBits.join(" · ")}
              </div>
            );
          }
          return null;
        })()}

        {/* Shots toggle */}
        <div className="border-t pt-3">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded(!expanded)}
              className="text-sm"
            >
              {expanded ? (
                <ChevronUp className="h-4 w-4 mr-1" />
              ) : (
                <ChevronDown className="h-4 w-4 mr-1" />
              )}
              <Trans>Exemples ({field.shots.length})</Trans>
            </Button>
            {!readOnly && (
              <Button size="sm" variant="outline" onClick={onAddShot}>
                <Plus className="h-4 w-4 mr-1" />
                <Trans>Ajouter</Trans>
              </Button>
            )}
          </div>

          {expanded && field.shots.length > 0 && (
            <div className="mt-3 space-y-3">
              {field.shots.map((shot, idx) => (
                <div
                  key={shot.id}
                  className="border rounded-lg p-3 bg-slate-50"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-slate-600">
                      <Trans>Exemple {idx + 1}</Trans>
                    </span>
                    {!readOnly && (
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => onEditShot(shot)}
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                          onClick={() => onDeleteShot(shot.id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-xs text-slate-500 mb-1"><Trans>Entrée</Trans></div>
                      <pre className="bg-white border rounded p-2 text-xs overflow-auto max-h-24">
                        {shot.input.length > 200
                          ? shot.input.substring(0, 200) + "..."
                          : shot.input}
                      </pre>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500 mb-1"><Trans>Sortie</Trans></div>
                      <pre className="bg-white border rounded p-2 text-xs overflow-auto max-h-24">
                        {typeof shot.output === "string"
                          ? shot.output.length > 200
                            ? shot.output.substring(0, 200) + "..."
                            : shot.output
                          : JSON.stringify(shot.output, null, 2).substring(
                              0,
                              200,
                            )}
                      </pre>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {expanded && field.shots.length === 0 && (
            <div className="mt-3 text-center py-4 text-slate-400 text-sm">
              <Trans>Aucun exemple. Cliquez sur "Ajouter" pour créer un exemple.</Trans>
            </div>
          )}
        </div>

        {/* Les retours cliniciens, sous les exemples et pas ailleurs.
            C'est la matière première de l'exemple qui est juste au-dessus : ce qu'un médecin a
            corrigé, le mot qu'il a écrit et que la liste déclarée ne sait pas rendre, et
            l'extrait qu'il a désigné. Les tenir dans deux écrans différents est ce qui a fait
            que 830 propositions sont restées sans lecteur.
            Replié par défaut et chargé à l'ouverture : une ontologie porte cinquante champs. */}
        <div className="border-t pt-3 mt-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setFeedbackOpen(!feedbackOpen)}
            className="text-sm"
          >
            {feedbackOpen ? (
              <ChevronUp className="h-4 w-4 mr-1" />
            ) : (
              <ChevronDown className="h-4 w-4 mr-1" />
            )}
            <Trans>Retours cliniciens</Trans>
            {field.pendingProposals > 0 && (
              <span className="ml-2 inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                {field.pendingProposals}
              </span>
            )}
          </Button>
          {feedbackOpen && (
            <div className="mt-3">
              <ClinicianFeedbackPanel
                stackId={stackId}
                fieldId={field.id}
                fieldKey={field.fieldKey}
                readOnly={readOnly}
                onShotsChanged={onShotsChanged}
              />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function OntologyManager({
  fields,
  onAddField,
  onUpdateField,
  onDeleteField,
  onAddShot,
  onUpdateShot,
  onDeleteShot,
  onDeployToSpecialite,
  deployingToSpecialite,
  specialite,
  onEmbedShots,
  embeddingShots,
  onOptimizeField,
  stackId,
  onShotsChanged,
  focusFieldId,
  readOnly,
}: OntologyManagerProps) {
  const { t } = useLingui();
  const [fieldEditorOpen, setFieldEditorOpen] = useState(false);
  const [editingField, setEditingField] = useState<OntologyField | null>(null);
  const [shotEditorOpen, setShotEditorOpen] = useState(false);
  const [editingShot, setEditingShot] = useState<{
    fieldId: string;
    shot: OntologyShot | null;
  } | null>(null);

  const handleSaveField = (data: Omit<OntologyFieldDraft, "shots">) => {
    if (editingField) {
      onUpdateField(editingField.id, data);
    } else {
      onAddField({ ...data, shots: [] });
    }
    setEditingField(null);
  };

  const handleSaveShot = (data: Omit<OntologyShot, "id">) => {
    if (!editingShot) return;
    if (editingShot.shot) {
      onUpdateShot(editingShot.fieldId, editingShot.shot.id, data);
    } else {
      onAddShot(editingShot.fieldId, data);
    }
    setEditingShot(null);
  };

  if (fields.length === 0) {
    return (
      <div className="text-center py-12">
        <BookOpen className="h-12 w-12 mx-auto text-slate-300 mb-3" />
        <p className="text-slate-500"><Trans>Aucun champ d'ontologie défini.</Trans></p>
        <p className="text-sm text-slate-400 mt-1">
          <Trans>L'ontologie définit les champs et leurs exemples (few-shot) pour guider le modèle.</Trans>
        </p>
        {!readOnly && (
          <div className="flex items-center justify-center gap-3 mt-4 flex-wrap">
            {onEmbedShots && (
              <Button
                variant="outline"
                onClick={onEmbedShots}
                disabled={embeddingShots}
              >
                {embeddingShots ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Brain className="h-4 w-4 mr-1" />
                )}
                {embeddingShots ? t`Calcul en cours...` : t`Calculer les embeddings RAG`}
              </Button>
            )}
            <Button variant="outline" onClick={() => setFieldEditorOpen(true)}>
              <Plus className="h-4 w-4 mr-1" />
              <Trans>Ajouter un champ</Trans>
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Layers className="h-5 w-5 text-primary" />
          <span className="font-medium text-slate-700">
            <Trans>{fields.length} champs d'ontologie</Trans>
          </span>
        </div>
        {!readOnly && (
          <div className="flex gap-2 flex-wrap">
            {onDeployToSpecialite && specialite && (
              <Button
                variant="outline"
                onClick={() => {
                  if (
                    confirm(
                      t`Déployer en production pour la spécialité « ${specialite} » ? Les champs et exemples existants seront mis à jour.`,
                    )
                  ) {
                    onDeployToSpecialite();
                  }
                }}
                disabled={deployingToSpecialite}
                className="border-primary/50 text-primary hover:bg-primary/5"
              >
                {deployingToSpecialite ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4 mr-1" />
                )}
                <Trans>Déployer en production</Trans>
              </Button>
            )}
            {onEmbedShots && (
              <Button
                variant="outline"
                onClick={onEmbedShots}
                disabled={embeddingShots}
              >
                {embeddingShots ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Brain className="h-4 w-4 mr-1" />
                )}
                {embeddingShots ? t`Calcul en cours...` : t`Calculer les embeddings RAG`}
              </Button>
            )}
            <Button onClick={() => setFieldEditorOpen(true)}>
              <Plus className="h-4 w-4 mr-1" />
              <Trans>Ajouter un champ</Trans>
            </Button>
          </div>
        )}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-primary shadow-sm bg-white">
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-primary">
              {fields.filter((f) => f.fieldType === "patient").length}
            </div>
            <div className="text-xs text-slate-500"><Trans>Champs Patient</Trans></div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-primary shadow-sm bg-white">
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-green-600">
              {fields.filter((f) => f.fieldType === "content").length}
            </div>
            <div className="text-xs text-slate-500"><Trans>Champs Contenu</Trans></div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-primary shadow-sm bg-white">
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-purple-600">
              {fields.reduce((acc, f) => acc + f.shots.length, 0)}
            </div>
            <div className="text-xs text-slate-500"><Trans>Exemples total</Trans></div>
          </CardContent>
        </Card>
      </div>

      {/* Field cards grouped by groupName. Emoji comes from the ontology's own
          ``group_emoji`` column — same source of truth as the patient page and
          the configuration "Données extraites" view. First non-null value per
          group wins. */}
      {(() => {
        const grouped: Record<string, OntologyField[]> = {};
        for (const field of fields) {
          const group = field.groupName || "Autre";
          if (!grouped[group]) grouped[group] = [];
          grouped[group].push(field);
        }
        return Object.entries(grouped).map(([group, groupFields]) => {
          const emoji =
            groupFields.find((f) => f.groupEmoji)?.groupEmoji ||
            DEFAULT_GROUP_EMOJI;
          return (
          <div key={group} className="space-y-3">
            <div className="flex items-center gap-2 pt-4 pb-1 border-b border-slate-200">
              <span className="text-lg">{emoji}</span>
              <h3 className="text-sm font-semibold text-slate-700">{group}</h3>
              <Badge variant="outline" className="text-xs">
                <Trans>{groupFields.length} champs</Trans>
              </Badge>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {groupFields.map((field) => (
                <OntologyFieldCard
                  key={field.id}
                  field={field}
                  stackId={stackId}
                  onShotsChanged={onShotsChanged}
                  focused={field.id === focusFieldId}
                  readOnly={readOnly}
                  onEdit={() => {
                    setEditingField(field);
                    setFieldEditorOpen(true);
                  }}
                  onDelete={() => {
                    if (
                      confirm(
                        t`Supprimer le champ "${field.name}" et ses ${field.shots.length} exemples ?`,
                      )
                    ) {
                      onDeleteField(field.id);
                    }
                  }}
                  onAddShot={() => {
                    setEditingShot({ fieldId: field.id, shot: null });
                    setShotEditorOpen(true);
                  }}
                  onEditShot={(shot) => {
                    setEditingShot({ fieldId: field.id, shot });
                    setShotEditorOpen(true);
                  }}
                  onDeleteShot={(shotId) => {
                    if (confirm(t`Supprimer cet exemple ?`)) {
                      onDeleteShot(field.id, shotId);
                    }
                  }}
                  onOptimize={
                    onOptimizeField
                      ? () => onOptimizeField(field.id)
                      : undefined
                  }
                />
              ))}
            </div>
          </div>
          );
        });
      })()}

      {/* Field editor dialog */}
      <FieldEditorDialog
        key={editingField?.id || "new"}
        open={fieldEditorOpen}
        onClose={() => {
          setFieldEditorOpen(false);
          setEditingField(null);
        }}
        onSave={handleSaveField}
        initialData={editingField || undefined}
        title={editingField ? t`Modifier le champ` : t`Ajouter un champ`}
      />

      {/* Shot editor dialog */}
      {editingShot && (
        <ShotEditorDialog
          open={shotEditorOpen}
          onClose={() => {
            setShotEditorOpen(false);
            setEditingShot(null);
          }}
          onSave={handleSaveShot}
          initialData={editingShot.shot || undefined}
          title={editingShot.shot ? t`Modifier l'exemple` : t`Ajouter un exemple`}
        />
      )}
    </div>
  );
}
